/**
 * Net Worth Tracker Page - Auxilio Finance
 * Track assets and liabilities with Quicken QIF import/export support
 */

import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PlusIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ArrowUpTrayIcon,
  SparklesIcon,
  ChartBarIcon,
  TrashIcon,
  PencilIcon,
  XMarkIcon,
  BuildingLibraryIcon,
  HomeIcon,
  BanknotesIcon,
  CreditCardIcon,
  TruckIcon,
  CurrencyDollarIcon,
  ChartPieIcon,
} from '@heroicons/react/24/outline';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Line,
  Area,
  ComposedChart,
} from 'recharts';
import apiClient from '../services/api';
import { Button } from '../components/ui/Button';
import LoadingOverlay from '../components/ui/LoadingOverlay';
import { ExportDropdownMenu } from '../components/ui/ExportDropdownMenu';

interface Asset {
  id: string;
  name: string;
  type_id?: string;
  type?: string;  // Backend uses 'type' field
  value: number;
  institution?: string;
  account_number?: string;
  notes?: string;
  last_updated?: string;
  created_at?: string;
  updated_at?: string;
}

interface Liability {
  id: string;
  name: string;
  type_id?: string;
  type?: string;  // Backend uses 'type' field
  balance: number;
  interest_rate?: number;
  minimum_payment?: number;
  institution?: string;
  account_number?: string;
  due_date?: number;
  notes?: string;
  last_updated?: string;
  created_at?: string;
  updated_at?: string;
}

interface AssetType {
  id: string;
  name: string;
  icon: string;
  group: string;
  quicken_category: string;
}

interface LiabilityType {
  id: string;
  name: string;
  icon: string;
  group: string;
  quicken_category: string;
}

interface NetWorthSummary {
  total_assets: number;
  total_liabilities: number;
  net_worth: number;
  assets_by_type: Record<string, number>;
  liabilities_by_type: Record<string, number>;
  asset_count: number;
  liability_count: number;
}

interface AIAnalysis {
  summary: {
    net_worth: number;
    total_assets: number;
    total_liabilities: number;
  };
  strengths: string[];
  areas_for_improvement: string[];
  recommendations: string[];
  asset_allocation_analysis: string;
  debt_analysis: string;
  risk_assessment: string;
}

const ASSET_COLORS = [
  '#10b981', '#3b82f6', '#8b5cf6', '#06b6d4', '#84cc16',
  '#14b8a6', '#0ea5e9', '#a855f7', '#22c55e', '#6366f1',
];

const LIABILITY_COLORS = [
  '#ef4444', '#f97316', '#f59e0b', '#ec4899', '#dc2626',
  '#ea580c', '#d97706', '#db2777', '#b91c1c', '#c2410c',
];

export const NetWorthTracker: React.FC = () => {
  const { t } = useTranslation();

  // State
  const [activeTab, setActiveTab] = useState<'overview' | 'assets' | 'liabilities' | 'analysis'>('overview');
  const [assets, setAssets] = useState<Asset[]>([]);
  const [liabilities, setLiabilities] = useState<Liability[]>([]);
  const [assetTypes, setAssetTypes] = useState<AssetType[]>([]);
  const [liabilityTypes, setLiabilityTypes] = useState<LiabilityType[]>([]);
  const [summary, setSummary] = useState<NetWorthSummary | null>(null);
  const [trends, setTrends] = useState<any[]>([]);
  const [analysis, setAnalysis] = useState<AIAnalysis | null>(null);
  const [loading, setLoading] = useState(false);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingOverlay, setLoadingOverlay] = useState<{ isVisible: boolean; message: string; subMessage?: string }>({
    isVisible: false,
    message: '',
    subMessage: undefined
  });

  // Sorting state - default to descending (highest first)
  const [assetSortOrder, setAssetSortOrder] = useState<'asc' | 'desc'>('desc');
  const [liabilitySortOrder, setLiabilitySortOrder] = useState<'asc' | 'desc'>('desc');

  // Modal state
  const [showAddAssetModal, setShowAddAssetModal] = useState(false);
  const [showAddLiabilityModal, setShowAddLiabilityModal] = useState(false);
  const [editingAsset, setEditingAsset] = useState<Asset | null>(null);
  const [editingLiability, setEditingLiability] = useState<Liability | null>(null);

  const [assetForm, setAssetForm] = useState({
    name: '',
    type_id: '',
    value: '',
    institution: '',
    account_number: '',
    notes: '',
  });

  const [liabilityForm, setLiabilityForm] = useState({
    name: '',
    type_id: '',
    balance: '',
    interest_rate: '',
    minimum_payment: '',
    institution: '',
    account_number: '',
    due_date: '',
    notes: '',
  });

  // File input ref for import
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load types on mount
  useEffect(() => {
    loadTypes();
  }, []);

  // Load data after types are loaded
  useEffect(() => {
    if (assetTypes.length > 0) {
      loadData();
    }
  }, [assetTypes]);

  const loadTypes = async () => {
    try {
      const response = await apiClient.get('/net-worth/categories');
      setAssetTypes(response.data.asset_categories);
      setLiabilityTypes(response.data.liability_categories);
    } catch (err) {
      console.error('Failed to load categories:', err);
    }
  };

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [assetsRes, liabilitiesRes, summaryRes, trendsRes] = await Promise.all([
        apiClient.get('/net-worth/assets'),
        apiClient.get('/net-worth/liabilities'),
        apiClient.get('/net-worth/summary'),
        apiClient.get('/net-worth/trends', { params: { months: 12 } }),
      ]);

      setAssets(assetsRes.data.assets);
      setLiabilities(liabilitiesRes.data.liabilities);
      setSummary(summaryRes.data);
      setTrends(trendsRes.data.trends);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load net worth data');
    } finally {
      setLoading(false);
    }
  };

  const loadAnalysis = async () => {
    setAnalysisLoading(true);
    try {
      const response = await apiClient.post('/net-worth/analyze');
      setAnalysis(response.data);
    } catch (err: any) {
      console.error('Failed to load analysis:', err);
    } finally {
      setAnalysisLoading(false);
    }
  };

  const handleAddAsset = async () => {
    if (!assetForm.name || !assetForm.type_id || !assetForm.value) {
      return;
    }

    try {
      const payload = {
        ...assetForm,
        value: parseFloat(assetForm.value),
      };

      if (editingAsset) {
        await apiClient.put(`/net-worth/assets/${editingAsset.id}`, payload);
      } else {
        await apiClient.post('/net-worth/assets', payload);
      }

      setShowAddAssetModal(false);
      setEditingAsset(null);
      resetAssetForm();
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save asset');
    }
  };

  const handleAddLiability = async () => {
    if (!liabilityForm.name || !liabilityForm.type_id || !liabilityForm.balance) {
      return;
    }

    try {
      const payload = {
        ...liabilityForm,
        balance: parseFloat(liabilityForm.balance),
        interest_rate: liabilityForm.interest_rate ? parseFloat(liabilityForm.interest_rate) : null,
        minimum_payment: liabilityForm.minimum_payment ? parseFloat(liabilityForm.minimum_payment) : null,
        due_date: liabilityForm.due_date ? parseInt(liabilityForm.due_date) : null,
      };

      if (editingLiability) {
        await apiClient.put(`/net-worth/liabilities/${editingLiability.id}`, payload);
      } else {
        await apiClient.post('/net-worth/liabilities', payload);
      }

      setShowAddLiabilityModal(false);
      setEditingLiability(null);
      resetLiabilityForm();
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save liability');
    }
  };

  const handleDeleteAsset = async (id: string) => {
    if (!window.confirm(t('netWorth:confirmDeleteAsset', 'Are you sure you want to delete this asset?'))) {
      return;
    }

    try {
      await apiClient.delete(`/net-worth/assets/${id}`);
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete asset');
    }
  };

  const handleDeleteLiability = async (id: string) => {
    if (!window.confirm(t('netWorth:confirmDeleteLiability', 'Are you sure you want to delete this liability?'))) {
      return;
    }

    try {
      await apiClient.delete(`/net-worth/liabilities/${id}`);
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete liability');
    }
  };

  const handleEditAsset = (asset: Asset) => {
    setEditingAsset(asset);
    setAssetForm({
      name: asset.name,
      type_id: asset.type_id || asset.type || '',
      value: asset.value.toString(),
      institution: asset.institution || '',
      account_number: asset.account_number || '',
      notes: asset.notes || '',
    });
    setShowAddAssetModal(true);
  };

  const handleEditLiability = (liability: Liability) => {
    setEditingLiability(liability);
    setLiabilityForm({
      name: liability.name,
      type_id: liability.type_id || liability.type || '',
      balance: liability.balance.toString(),
      interest_rate: liability.interest_rate?.toString() || '',
      minimum_payment: liability.minimum_payment?.toString() || '',
      institution: liability.institution || '',
      account_number: liability.account_number || '',
      due_date: liability.due_date?.toString() || '',
      notes: liability.notes || '',
    });
    setShowAddLiabilityModal(true);
  };

  const resetAssetForm = () => {
    setAssetForm({
      name: '',
      type_id: '',
      value: '',
      institution: '',
      account_number: '',
      notes: '',
    });
  };

  const resetLiabilityForm = () => {
    setLiabilityForm({
      name: '',
      type_id: '',
      balance: '',
      interest_rate: '',
      minimum_payment: '',
      institution: '',
      account_number: '',
      due_date: '',
      notes: '',
    });
  };

  const handleImportQuicken = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    const isPdf = file.name.toLowerCase().endsWith('.pdf');
    setLoadingOverlay({
      isVisible: true,
      message: t('netWorth:importing', 'Importing Net Worth Data...'),
      subMessage: isPdf
        ? t('netWorth:importingPdfHint', 'Processing PDF (this may take a moment for scanned documents)')
        : t('netWorth:importingCsvHint', 'Analyzing and extracting net worth information')
    });

    try {
      await apiClient.post('/net-worth/import/quicken', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to import file');
    } finally {
      setLoadingOverlay({ isVisible: false, message: '', subMessage: undefined });
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await apiClient.get('/net-worth/export/csv', {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `auxilio_net_worth_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err: any) {
      setError('Failed to export CSV');
    }
  };

  // Generate export content for Word/PDF/Text
  const generateExportContent = () => {
    let content = `Net Worth Summary\n`;
    content += `Generated: ${new Date().toLocaleString()}\n\n`;
    content += `=== SUMMARY ===\n`;
    content += `Total Assets: ${formatCurrency(summary?.total_assets || 0)}\n`;
    content += `Total Liabilities: ${formatCurrency(summary?.total_liabilities || 0)}\n`;
    content += `Net Worth: ${formatCurrency(summary?.net_worth || 0)}\n\n`;

    content += `=== ASSETS ===\n`;
    assets.forEach(asset => {
      content += `${asset.name}: ${formatCurrency(asset.value)}`;
      if (asset.type) content += ` (${asset.type})`;
      content += `\n`;
    });

    content += `\n=== LIABILITIES ===\n`;
    liabilities.forEach(liability => {
      content += `${liability.name}: ${formatCurrency(liability.balance)}`;
      if (liability.type) content += ` (${liability.type})`;
      content += `\n`;
    });

    content += `\n---\nGenerated by Auxilio Finance\n`;
    return content;
  };

  // Handle export to Word/PDF/Text
  const handleExport = async (format: 'word' | 'pdf' | 'text' | 'csv') => {
    if (format === 'csv') {
      handleExportCSV();
      return;
    }

    const content = generateExportContent();
    const title = `Net Worth Summary - ${new Date().toISOString().slice(0, 10)}`;

    const formatMap: Record<string, string> = {
      'word': 'docx',
      'pdf': 'pdf',
      'text': 'txt',
    };

    try {
      const response = await apiClient.post('/export/document', {
        content: content,
        title: title,
        format: formatMap[format],
      }, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${title}.${formatMap[format]}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to export document:', err);
      setError('Failed to export document');
    }
  };

  // Handle print
  const handlePrint = () => {
    const content = generateExportContent();
    const title = `Net Worth Summary - ${new Date().toISOString().slice(0, 10)}`;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>${title}</title>
            <style>
              body { font-family: Arial, sans-serif; padding: 40px; line-height: 1.6; }
              h1 { color: #d97706; border-bottom: 2px solid #d97706; padding-bottom: 10px; }
              pre { white-space: pre-wrap; font-family: inherit; }
            </style>
          </head>
          <body>
            <pre>${content}</pre>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  const handleSaveSnapshot = async () => {
    try {
      await apiClient.post('/net-worth/snapshots');
      alert(t('netWorth:snapshotSaved', 'Net worth snapshot saved successfully!'));
      loadData(); // Reload data to show the new snapshot in trends
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      // Handle FastAPI validation error format (array of objects)
      const errorMessage = Array.isArray(detail)
        ? detail.map((d: any) => d.msg || String(d)).join(', ')
        : (typeof detail === 'string' ? detail : 'Failed to save snapshot');
      setError(errorMessage);
    }
  };

  const handleClearAllData = async () => {
    if (!window.confirm(t('netWorth:confirmClearAll', 'Are you sure you want to clear ALL net worth data? This cannot be undone.'))) {
      return;
    }

    try {
      setLoading(true);
      const response = await apiClient.delete('/net-worth/clear-all');
      alert(response.data.message);
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to clear data');
    } finally {
      setLoading(false);
    }
  };

  const getAssetTypeName = (typeId: string): string => {
    const type = assetTypes.find((t) => t.id === typeId);
    return type?.name || typeId;
  };

  const getLiabilityTypeName = (typeId: string): string => {
    const type = liabilityTypes.find((t) => t.id === typeId);
    return type?.name || typeId;
  };

  const getTypeIcon = (typeId: string, isAsset: boolean) => {
    const iconMap: Record<string, any> = {
      checking: BanknotesIcon,
      savings: BuildingLibraryIcon,
      cash: CurrencyDollarIcon,
      real_estate: HomeIcon,
      vehicle: TruckIcon,
      credit_card: CreditCardIcon,
    };
    const Icon = iconMap[typeId] || (isAsset ? BanknotesIcon : CreditCardIcon);
    return <Icon className="h-5 w-5" />;
  };

  // Prepare chart data with type ID and accounts for tooltip lookups
  const assetChartData = Object.entries(summary?.assets_by_type || {}).map(([id, value]) => ({
    name: getAssetTypeName(id),
    value: value,
    typeId: id,
    accounts: assets.filter(a => a.type === id || a.type_id === id),
  }));

  const liabilityChartData = Object.entries(summary?.liabilities_by_type || {}).map(([id, value]) => ({
    name: getLiabilityTypeName(id),
    value: value,
    typeId: id,
    accounts: liabilities.filter(l => l.type === id || l.type_id === id),
  }));

  // Custom tooltip component for asset pie chart
  const CustomAssetTooltip = ({ active, payload }: any) => {
    if (!active || !payload || payload.length === 0) {
      return null;
    }
    const data = payload[0].payload;
    const accountsOfType: Asset[] = data.accounts || [];

    return (
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-3 max-w-xs z-50">
        <p className="font-semibold text-gray-900 dark:text-white mb-2">{data.name}</p>
        {accountsOfType.length > 0 ? (
          <div className="space-y-1">
            {accountsOfType.map((account, idx) => (
              <div key={idx} className="flex justify-between text-sm gap-4">
                <span className="text-gray-600 dark:text-gray-400 truncate">{account.name}</span>
                <span className="text-gray-900 dark:text-white font-medium whitespace-nowrap">
                  {formatCurrency(account.value)}
                </span>
              </div>
            ))}
            <div className="border-t border-gray-200 dark:border-gray-600 pt-1 mt-1 flex justify-between text-sm font-semibold">
              <span className="text-gray-700 dark:text-gray-300">{t('netWorth:total', 'Total')}</span>
              <span className="text-amber-600 dark:text-amber-400">{formatCurrency(data.value)}</span>
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-500 dark:text-gray-400">{formatCurrency(data.value)}</p>
        )}
      </div>
    );
  };

  // Custom tooltip component for liability pie chart
  const CustomLiabilityTooltip = ({ active, payload }: any) => {
    if (!active || !payload || payload.length === 0) {
      return null;
    }
    const data = payload[0].payload;
    const accountsOfType: Liability[] = data.accounts || [];

    return (
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-3 max-w-xs z-50">
        <p className="font-semibold text-gray-900 dark:text-white mb-2">{data.name}</p>
        {accountsOfType.length > 0 ? (
          <div className="space-y-1">
            {accountsOfType.map((account, idx) => (
              <div key={idx} className="flex justify-between text-sm gap-4">
                <span className="text-gray-600 dark:text-gray-400 truncate">{account.name}</span>
                <span className="text-gray-900 dark:text-white font-medium whitespace-nowrap">
                  {formatCurrency(account.balance)}
                </span>
              </div>
            ))}
            <div className="border-t border-gray-200 dark:border-gray-600 pt-1 mt-1 flex justify-between text-sm font-semibold">
              <span className="text-gray-700 dark:text-gray-300">{t('netWorth:total', 'Total')}</span>
              <span className="text-red-600 dark:text-red-400">{formatCurrency(data.value)}</span>
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-500 dark:text-gray-400">{formatCurrency(data.value)}</p>
        )}
      </div>
    );
  };

  // Custom tooltip for the trend chart showing all values including net worth
  const CustomTrendTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload || payload.length === 0) {
      return null;
    }

    // Find values from payload
    const assetsEntry = payload.find((p: any) => p.dataKey === 'total_assets');
    const liabilitiesEntry = payload.find((p: any) => p.dataKey === 'total_liabilities');
    const netWorthEntry = payload.find((p: any) => p.dataKey === 'net_worth');

    return (
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-3 min-w-[180px]">
        <p className="font-semibold text-gray-900 dark:text-white mb-2 border-b border-gray-200 dark:border-gray-600 pb-1">{label}</p>
        <div className="space-y-1">
          {assetsEntry && (
            <div className="flex justify-between text-sm gap-4">
              <span className="text-green-600 dark:text-green-400 flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-green-500"></span>
                {t('netWorth:assets', 'Assets')}
              </span>
              <span className="text-gray-900 dark:text-white font-medium">
                {formatCurrency(assetsEntry.value)}
              </span>
            </div>
          )}
          {liabilitiesEntry && (
            <div className="flex justify-between text-sm gap-4">
              <span className="text-red-600 dark:text-red-400 flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-red-500"></span>
                {t('netWorth:liabilities', 'Liabilities')}
              </span>
              <span className="text-gray-900 dark:text-white font-medium">
                {formatCurrency(liabilitiesEntry.value)}
              </span>
            </div>
          )}
          {netWorthEntry && (
            <div className="flex justify-between text-sm gap-4 border-t border-gray-200 dark:border-gray-600 pt-1 mt-1">
              <span className="text-amber-600 dark:text-amber-400 font-semibold flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                {t('netWorth:netWorth', 'Net Worth')}
              </span>
              <span className="text-amber-600 dark:text-amber-400 font-bold">
                {formatCurrency(netWorthEntry.value)}
              </span>
            </div>
          )}
        </div>
      </div>
    );
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Sorted assets and liabilities
  const sortedAssets = [...assets].sort((a, b) => {
    return assetSortOrder === 'desc' ? b.value - a.value : a.value - b.value;
  });

  const sortedLiabilities = [...liabilities].sort((a, b) => {
    return liabilitySortOrder === 'desc' ? b.balance - a.balance : a.balance - b.balance;
  });

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Loading Overlay */}
      <LoadingOverlay
        isVisible={loadingOverlay.isVisible}
        message={loadingOverlay.message}
        subMessage={loadingOverlay.subMessage}
      />

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <ChartPieIcon className="h-8 w-8 text-amber-500" />
            {t('netWorth:title', 'Net Worth Tracker')}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            {t('netWorth:subtitle', 'Track your assets and liabilities with AI-powered analysis')}
          </p>
        </div>

        <div className="flex items-center gap-3 mt-4 md:mt-0">
          {/* Import/Export/Clear */}
          <div className="flex gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf,.pdf,.qif,.qfx,.ofx,.csv,text/csv,application/x-qif"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  const ext = file.name.toLowerCase().split('.').pop();
                  if (!['qif', 'qfx', 'ofx', 'csv', 'pdf'].includes(ext || '')) {
                    alert('Please select a QIF, QFX, OFX, CSV, or PDF file.');
                    if (fileInputRef.current) fileInputRef.current.value = '';
                    return;
                  }
                }
                handleImportQuicken(e);
              }}
              className="hidden"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              className="p-2 text-gray-600 dark:text-gray-400 hover:text-amber-500 dark:hover:text-amber-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              title={t('netWorth:importQuicken', 'Import from Quicken or PDF (QIF/QFX/OFX/CSV/PDF)')}
            >
              <ArrowUpTrayIcon className="h-5 w-5" />
            </button>
            <ExportDropdownMenu
              onExport={handleExport}
              onPrint={handlePrint}
              onExportCSV={handleExportCSV}
              showCSV={true}
              showPrint={true}
            />
            <button
              onClick={handleClearAllData}
              className="p-2 text-gray-600 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              title={t('netWorth:clearAllData', 'Clear All Data (for testing)')}
            >
              <TrashIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Save Snapshot */}
          <Button variant="secondary" onClick={handleSaveSnapshot}>
            <ChartBarIcon className="h-5 w-5 mr-1" />
            {t('netWorth:saveSnapshot', 'Save Snapshot')}
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 dark:border-gray-700 mb-6">
        {(['overview', 'assets', 'liabilities', 'analysis'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => {
              setActiveTab(tab);
              if (tab === 'analysis' && !analysis) {
                loadAnalysis();
              }
            }}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab
                ? 'border-amber-500 text-amber-600 dark:text-amber-400'
                : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            {tab === 'overview' && t('netWorth:tabs.overview', 'Overview')}
            {tab === 'assets' && t('netWorth:tabs.assets', 'Assets')}
            {tab === 'liabilities' && t('netWorth:tabs.liabilities', 'Liabilities')}
            {tab === 'analysis' && (
              <span className="flex items-center gap-1">
                <SparklesIcon className="h-4 w-4" />
                {t('netWorth:tabs.analysis', 'AI Analysis')}
              </span>
            )}
          </button>
        ))}
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300">
          {error}
          <button onClick={() => setError(null)} className="ml-2 underline">
            Dismiss
          </button>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-amber-500 border-t-transparent"></div>
        </div>
      ) : (
        <>
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-600 dark:text-gray-400 text-sm">
                      {t('netWorth:totalAssets', 'Total Assets')}
                    </span>
                    <ArrowUpIcon className="h-5 w-5 text-green-500" />
                  </div>
                  <p className="text-3xl font-bold text-green-600 dark:text-green-400">
                    {formatCurrency(summary?.total_assets || 0)}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {summary?.asset_count || 0} {t('netWorth:accounts', 'accounts')}
                  </p>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-600 dark:text-gray-400 text-sm">
                      {t('netWorth:totalLiabilities', 'Total Liabilities')}
                    </span>
                    <ArrowDownIcon className="h-5 w-5 text-red-500" />
                  </div>
                  <p className="text-3xl font-bold text-red-600 dark:text-red-400">
                    {formatCurrency(summary?.total_liabilities || 0)}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {summary?.liability_count || 0} {t('netWorth:debts', 'debts')}
                  </p>
                </div>

                <div className="bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl p-6 text-white">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-amber-100 text-sm">
                      {t('netWorth:netWorth', 'Net Worth')}
                    </span>
                    <ChartPieIcon className="h-5 w-5 text-amber-200" />
                  </div>
                  <p className="text-3xl font-bold">
                    {formatCurrency(summary?.net_worth || 0)}
                  </p>
                  <p className="text-sm text-amber-100 mt-1">
                    {((summary?.total_liabilities || 0) / (summary?.total_assets || 1) * 100).toFixed(1)}% {t('netWorth:debtRatio', 'debt-to-asset ratio')}
                  </p>
                </div>
              </div>

              {/* Charts */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Asset Breakdown Pie Chart */}
                <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    {t('netWorth:assetBreakdown', 'Asset Breakdown')}
                  </h3>
                  {assetChartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={assetChartData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          outerRadius={100}
                          fill="#8884d8"
                          dataKey="value"
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        >
                          {assetChartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={ASSET_COLORS[index % ASSET_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip content={<CustomAssetTooltip />} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-64 text-gray-500 dark:text-gray-400">
                      {t('netWorth:noAssets', 'No assets recorded yet')}
                    </div>
                  )}
                </div>

                {/* Liability Breakdown Pie Chart */}
                <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    {t('netWorth:liabilityBreakdown', 'Liability Breakdown')}
                  </h3>
                  {liabilityChartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={liabilityChartData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          outerRadius={100}
                          fill="#8884d8"
                          dataKey="value"
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        >
                          {liabilityChartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={LIABILITY_COLORS[index % LIABILITY_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip content={<CustomLiabilityTooltip />} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-64 text-gray-500 dark:text-gray-400">
                      {t('netWorth:noLiabilities', 'No liabilities recorded yet')}
                    </div>
                  )}
                </div>
              </div>

              {/* Net Worth Trend */}
              <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  {t('netWorth:netWorthTrend', 'Net Worth Trend')}
                </h3>
                {trends.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <ComposedChart data={trends}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis
                        tickFormatter={(value) => {
                          if (Math.abs(value) >= 1000000) {
                            return `$${(value / 1000000).toFixed(1)}M`;
                          } else if (Math.abs(value) >= 1000) {
                            return `$${(value / 1000).toFixed(0)}k`;
                          }
                          return `$${value}`;
                        }}
                      />
                      <Tooltip content={<CustomTrendTooltip />} />
                      <Legend />
                      <Area type="monotone" dataKey="total_assets" name={t('netWorth:assets', 'Assets')} stroke="#10b981" fill="#10b98140" />
                      <Area type="monotone" dataKey="total_liabilities" name={t('netWorth:liabilities', 'Liabilities')} stroke="#ef4444" fill="#ef444440" />
                      <Line
                        type="monotone"
                        dataKey="net_worth"
                        name={t('netWorth:netWorth', 'Net Worth')}
                        stroke="#f59e0b"
                        strokeWidth={3}
                        dot={{ fill: '#f59e0b', stroke: '#fff', strokeWidth: 2, r: 6 }}
                        activeDot={{ fill: '#f59e0b', stroke: '#fff', strokeWidth: 3, r: 8 }}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-64 text-gray-500 dark:text-gray-400">
                    <div className="text-center">
                      <ChartBarIcon className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>{t('netWorth:noTrends', 'Save snapshots to track your net worth over time')}</p>
                      <Button onClick={handleSaveSnapshot} className="mt-4">
                        {t('netWorth:saveFirstSnapshot', 'Save First Snapshot')}
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Quicken Integration Info */}
              <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-4 border border-amber-200 dark:border-amber-800">
                <h3 className="font-semibold text-amber-800 dark:text-amber-200 mb-2">
                  {t('netWorth:quickenIntegration', 'Quicken Integration')}
                </h3>
                <p className="text-amber-700 dark:text-amber-300 text-sm">
                  {t('netWorth:quickenInfo', 'Import your accounts from Quicken using QIF files or export your Auxilio Finance data to Quicken. Account types are automatically mapped for seamless integration.')}
                </p>
                <div className="flex gap-4 mt-3">
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="text-amber-600 dark:text-amber-400 text-sm font-medium hover:underline"
                  >
                    {t('netWorth:importFromQuicken', 'Import from Quicken')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Assets Tab */}
          {activeTab === 'assets' && (
            <div className="space-y-4">
              {/* Add Asset Button and Sort Toggle */}
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-4">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                    {t('netWorth:yourAssets', 'Your Assets')}
                  </h2>
                  {assets.length > 1 && (
                    <button
                      onClick={() => setAssetSortOrder(assetSortOrder === 'desc' ? 'asc' : 'desc')}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-amber-500 dark:hover:text-amber-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors border border-gray-200 dark:border-gray-600"
                      title={assetSortOrder === 'desc'
                        ? t('netWorth:sortAscending', 'Sort by lowest value first')
                        : t('netWorth:sortDescending', 'Sort by highest value first')}
                    >
                      {assetSortOrder === 'desc' ? (
                        <>
                          <ArrowDownIcon className="h-4 w-4" />
                          <span>{t('netWorth:highestFirst', 'Highest First')}</span>
                        </>
                      ) : (
                        <>
                          <ArrowUpIcon className="h-4 w-4" />
                          <span>{t('netWorth:lowestFirst', 'Lowest First')}</span>
                        </>
                      )}
                    </button>
                  )}
                </div>
                <Button onClick={() => { resetAssetForm(); setShowAddAssetModal(true); }}>
                  <PlusIcon className="h-5 w-5 mr-1" />
                  {t('netWorth:addAsset', 'Add Asset')}
                </Button>
              </div>

              {/* Assets List */}
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                {assets.length > 0 ? (
                  <div className="divide-y divide-gray-200 dark:divide-gray-700">
                    {sortedAssets.map((asset) => (
                      <div
                        key={asset.id}
                        className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors flex items-center justify-between"
                      >
                        <div className="flex items-center gap-4">
                          <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400">
                            {getTypeIcon(asset.type_id || asset.type || '', true)}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white">
                              {asset.name}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {getAssetTypeName(asset.type_id || asset.type || '')}
                              {asset.institution && ` • ${asset.institution}`}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center gap-4">
                          <span className="text-lg font-semibold text-green-600 dark:text-green-400">
                            {formatCurrency(asset.value)}
                          </span>
                          <div className="flex gap-1">
                            <button
                              onClick={() => handleEditAsset(asset)}
                              className="p-2 text-gray-500 hover:text-amber-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteAsset(asset.id)}
                              className="p-2 text-gray-500 hover:text-red-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-12 text-center text-gray-500 dark:text-gray-400">
                    <BanknotesIcon className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>{t('netWorth:noAssetsYet', 'No assets recorded yet')}</p>
                    <Button onClick={() => setShowAddAssetModal(true)} className="mt-4">
                      <PlusIcon className="h-5 w-5 mr-1" />
                      {t('netWorth:addFirstAsset', 'Add your first asset')}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Liabilities Tab */}
          {activeTab === 'liabilities' && (
            <div className="space-y-4">
              {/* Add Liability Button and Sort Toggle */}
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-4">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                    {t('netWorth:yourLiabilities', 'Your Liabilities')}
                  </h2>
                  {liabilities.length > 1 && (
                    <button
                      onClick={() => setLiabilitySortOrder(liabilitySortOrder === 'desc' ? 'asc' : 'desc')}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-amber-500 dark:hover:text-amber-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors border border-gray-200 dark:border-gray-600"
                      title={liabilitySortOrder === 'desc'
                        ? t('netWorth:sortAscending', 'Sort by lowest balance first')
                        : t('netWorth:sortDescending', 'Sort by highest balance first')}
                    >
                      {liabilitySortOrder === 'desc' ? (
                        <>
                          <ArrowDownIcon className="h-4 w-4" />
                          <span>{t('netWorth:highestFirst', 'Highest First')}</span>
                        </>
                      ) : (
                        <>
                          <ArrowUpIcon className="h-4 w-4" />
                          <span>{t('netWorth:lowestFirst', 'Lowest First')}</span>
                        </>
                      )}
                    </button>
                  )}
                </div>
                <Button onClick={() => { resetLiabilityForm(); setShowAddLiabilityModal(true); }}>
                  <PlusIcon className="h-5 w-5 mr-1" />
                  {t('netWorth:addLiability', 'Add Liability')}
                </Button>
              </div>

              {/* Liabilities List */}
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                {liabilities.length > 0 ? (
                  <div className="divide-y divide-gray-200 dark:divide-gray-700">
                    {sortedLiabilities.map((liability) => (
                      <div
                        key={liability.id}
                        className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors flex items-center justify-between"
                      >
                        <div className="flex items-center gap-4">
                          <div className="p-2 rounded-lg bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400">
                            {getTypeIcon(liability.type_id || liability.type || '', false)}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white">
                              {liability.name}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {getLiabilityTypeName(liability.type_id || liability.type || '')}
                              {liability.institution && ` • ${liability.institution}`}
                              {liability.interest_rate && ` • ${liability.interest_rate}% APR`}
                            </p>
                            {liability.minimum_payment && (
                              <p className="text-xs text-gray-400 dark:text-gray-500">
                                {t('netWorth:minPayment', 'Min payment')}: {formatCurrency(liability.minimum_payment)}/mo
                                {liability.due_date && ` • Due day ${liability.due_date}`}
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-4">
                          <span className="text-lg font-semibold text-red-600 dark:text-red-400">
                            {formatCurrency(liability.balance)}
                          </span>
                          <div className="flex gap-1">
                            <button
                              onClick={() => handleEditLiability(liability)}
                              className="p-2 text-gray-500 hover:text-amber-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteLiability(liability.id)}
                              className="p-2 text-gray-500 hover:text-red-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-12 text-center text-gray-500 dark:text-gray-400">
                    <CreditCardIcon className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>{t('netWorth:noLiabilitiesYet', 'No liabilities recorded yet')}</p>
                    <Button onClick={() => setShowAddLiabilityModal(true)} className="mt-4">
                      <PlusIcon className="h-5 w-5 mr-1" />
                      {t('netWorth:addFirstLiability', 'Add your first liability')}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* AI Analysis Tab */}
          {activeTab === 'analysis' && (
            <div className="space-y-6">
              {analysisLoading ? (
                <div className="flex justify-center items-center h-64">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-4 border-amber-500 border-t-transparent mx-auto mb-4"></div>
                    <p className="text-gray-600 dark:text-gray-400">
                      {t('netWorth:analyzingFinances', 'Analyzing your financial position...')}
                    </p>
                  </div>
                </div>
              ) : analysis ? (
                <>
                  {/* Summary */}
                  <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                      <SparklesIcon className="h-5 w-5 text-amber-500" />
                      {t('netWorth:financialSummary', 'Financial Summary')}
                    </h3>
                    <div className="grid grid-cols-3 gap-4 mb-4">
                      <div className="px-4 py-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                        <span className="text-sm text-gray-600 dark:text-gray-400">{t('netWorth:totalAssets', 'Total Assets')}</span>
                        <p className="text-xl font-bold text-green-600 dark:text-green-400">
                          {formatCurrency(analysis.summary.total_assets)}
                        </p>
                      </div>
                      <div className="px-4 py-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                        <span className="text-sm text-gray-600 dark:text-gray-400">{t('netWorth:totalLiabilities', 'Total Liabilities')}</span>
                        <p className="text-xl font-bold text-red-600 dark:text-red-400">
                          {formatCurrency(analysis.summary.total_liabilities)}
                        </p>
                      </div>
                      <div className="px-4 py-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                        <span className="text-sm text-gray-600 dark:text-gray-400">{t('netWorth:netWorth', 'Net Worth')}</span>
                        <p className="text-xl font-bold text-amber-600 dark:text-amber-400">
                          {formatCurrency(analysis.summary.net_worth)}
                        </p>
                      </div>
                    </div>
                    {analysis.risk_assessment && (
                      <p className="text-gray-700 dark:text-gray-300">{analysis.risk_assessment}</p>
                    )}
                  </div>

                  {/* Asset Allocation Analysis */}
                  {analysis.asset_allocation_analysis && (
                    <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                        Asset Allocation Analysis
                      </h3>
                      <p className="text-gray-700 dark:text-gray-300">{analysis.asset_allocation_analysis}</p>
                    </div>
                  )}

                  {/* Debt Analysis */}
                  {analysis.debt_analysis && (
                    <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                        Debt Analysis
                      </h3>
                      <p className="text-gray-700 dark:text-gray-300">{analysis.debt_analysis}</p>
                    </div>
                  )}

                  {/* Strengths */}
                  {analysis.strengths && analysis.strengths.length > 0 && (
                    <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-6 border border-green-200 dark:border-green-800">
                      <h3 className="text-lg font-semibold text-green-800 dark:text-green-200 mb-4">
                        {t('netWorth:strengths', 'Financial Strengths')}
                      </h3>
                      <ul className="space-y-2">
                        {analysis.strengths.map((strength, index) => (
                          <li key={index} className="flex items-start gap-2 text-green-700 dark:text-green-300">
                            <ArrowUpIcon className="h-5 w-5 mt-0.5 flex-shrink-0" />
                            {strength}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Areas for Improvement */}
                  {analysis.areas_for_improvement && analysis.areas_for_improvement.length > 0 && (
                    <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-6 border border-red-200 dark:border-red-800">
                      <h3 className="text-lg font-semibold text-red-800 dark:text-red-200 mb-4">
                        {t('netWorth:concerns', 'Areas of Concern')}
                      </h3>
                      <ul className="space-y-2">
                        {analysis.areas_for_improvement.map((concern, index) => (
                          <li key={index} className="flex items-start gap-2 text-red-700 dark:text-red-300">
                            <ArrowDownIcon className="h-5 w-5 mt-0.5 flex-shrink-0" />
                            {concern}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Recommendations */}
                  {analysis.recommendations && analysis.recommendations.length > 0 && (
                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-6 border border-blue-200 dark:border-blue-800">
                      <h3 className="text-lg font-semibold text-blue-800 dark:text-blue-200 mb-4">
                        {t('netWorth:recommendations', 'Recommendations')}
                      </h3>
                      <ul className="space-y-2">
                        {analysis.recommendations.map((rec, index) => (
                          <li key={index} className="flex items-start gap-2 text-blue-700 dark:text-blue-300">
                            <SparklesIcon className="h-5 w-5 mt-0.5 flex-shrink-0" />
                            {rec}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <button
                    onClick={loadAnalysis}
                    className="text-amber-600 dark:text-amber-400 text-sm font-medium hover:underline"
                  >
                    {t('netWorth:refreshAnalysis', 'Refresh AI Analysis')}
                  </button>
                </>
              ) : (
                <div className="text-center py-12">
                  <SparklesIcon className="h-12 w-12 mx-auto mb-4 text-amber-500" />
                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    {t('netWorth:noAnalysisYet', 'Click below to get AI-powered analysis of your financial position')}
                  </p>
                  <Button onClick={loadAnalysis}>
                    <SparklesIcon className="h-5 w-5 mr-1" />
                    {t('netWorth:getAnalysis', 'Get AI Analysis')}
                  </Button>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Add/Edit Asset Modal */}
      {showAddAssetModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                {editingAsset
                  ? t('netWorth:editAsset', 'Edit Asset')
                  : t('netWorth:addAsset', 'Add Asset')}
              </h2>
              <button
                onClick={() => { setShowAddAssetModal(false); setEditingAsset(null); }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('netWorth:assetName', 'Asset Name')} *
                </label>
                <input
                  type="text"
                  value={assetForm.name}
                  onChange={(e) => setAssetForm({ ...assetForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder={t('netWorth:assetNamePlaceholder', 'e.g., Chase Checking Account')}
                  required
                />
              </div>

              {/* Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('netWorth:assetType', 'Asset Type')} *
                </label>
                <select
                  value={assetForm.type_id}
                  onChange={(e) => setAssetForm({ ...assetForm, type_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  required
                >
                  <option value="">{t('netWorth:selectType', 'Select a type')}</option>
                  {assetTypes.map((type) => (
                    <option key={type.id} value={type.id}>{type.name}</option>
                  ))}
                </select>
              </div>

              {/* Value */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('netWorth:currentValue', 'Current Value')} *
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-gray-500 dark:text-gray-400">$</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={assetForm.value}
                    onChange={(e) => setAssetForm({ ...assetForm, value: e.target.value })}
                    className="w-full pl-7 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="0.00"
                    required
                  />
                </div>
              </div>

              {/* Institution */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('netWorth:institution', 'Institution')}
                </label>
                <input
                  type="text"
                  value={assetForm.institution}
                  onChange={(e) => setAssetForm({ ...assetForm, institution: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder={t('netWorth:institutionPlaceholder', 'e.g., Chase Bank')}
                />
              </div>

              {/* Account Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('netWorth:accountNumber', 'Account Number (last 4 digits)')}
                </label>
                <input
                  type="text"
                  value={assetForm.account_number}
                  onChange={(e) => setAssetForm({ ...assetForm, account_number: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="****1234"
                  maxLength={4}
                />
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('netWorth:notes', 'Notes')}
                </label>
                <textarea
                  value={assetForm.notes}
                  onChange={(e) => setAssetForm({ ...assetForm, notes: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  rows={2}
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button
                variant="secondary"
                onClick={() => { setShowAddAssetModal(false); setEditingAsset(null); }}
                fullWidth
              >
                {t('common:buttons.cancel', 'Cancel')}
              </Button>
              <Button
                onClick={handleAddAsset}
                fullWidth
                disabled={!assetForm.name || !assetForm.type_id || !assetForm.value}
              >
                {editingAsset
                  ? t('common:buttons.save', 'Save')
                  : t('netWorth:addAsset', 'Add Asset')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Liability Modal */}
      {showAddLiabilityModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                {editingLiability
                  ? t('netWorth:editLiability', 'Edit Liability')
                  : t('netWorth:addLiability', 'Add Liability')}
              </h2>
              <button
                onClick={() => { setShowAddLiabilityModal(false); setEditingLiability(null); }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('netWorth:liabilityName', 'Liability Name')} *
                </label>
                <input
                  type="text"
                  value={liabilityForm.name}
                  onChange={(e) => setLiabilityForm({ ...liabilityForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder={t('netWorth:liabilityNamePlaceholder', 'e.g., Chase Visa Credit Card')}
                  required
                />
              </div>

              {/* Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('netWorth:liabilityType', 'Liability Type')} *
                </label>
                <select
                  value={liabilityForm.type_id}
                  onChange={(e) => setLiabilityForm({ ...liabilityForm, type_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  required
                >
                  <option value="">{t('netWorth:selectType', 'Select a type')}</option>
                  {liabilityTypes.map((type) => (
                    <option key={type.id} value={type.id}>{type.name}</option>
                  ))}
                </select>
              </div>

              {/* Balance */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('netWorth:currentBalance', 'Current Balance')} *
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-gray-500 dark:text-gray-400">$</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={liabilityForm.balance}
                    onChange={(e) => setLiabilityForm({ ...liabilityForm, balance: e.target.value })}
                    className="w-full pl-7 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="0.00"
                    required
                  />
                </div>
              </div>

              {/* Interest Rate */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('netWorth:interestRate', 'Interest Rate (APR)')}
                </label>
                <div className="relative">
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={liabilityForm.interest_rate}
                    onChange={(e) => setLiabilityForm({ ...liabilityForm, interest_rate: e.target.value })}
                    className="w-full pr-7 pl-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="0.00"
                  />
                  <span className="absolute right-3 top-2 text-gray-500 dark:text-gray-400">%</span>
                </div>
              </div>

              {/* Minimum Payment */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('netWorth:minimumPayment', 'Minimum Monthly Payment')}
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-gray-500 dark:text-gray-400">$</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={liabilityForm.minimum_payment}
                    onChange={(e) => setLiabilityForm({ ...liabilityForm, minimum_payment: e.target.value })}
                    className="w-full pl-7 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="0.00"
                  />
                </div>
              </div>

              {/* Due Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('netWorth:dueDate', 'Due Date (day of month)')}
                </label>
                <input
                  type="number"
                  min="1"
                  max="31"
                  value={liabilityForm.due_date}
                  onChange={(e) => setLiabilityForm({ ...liabilityForm, due_date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="15"
                />
              </div>

              {/* Institution */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('netWorth:institution', 'Institution')}
                </label>
                <input
                  type="text"
                  value={liabilityForm.institution}
                  onChange={(e) => setLiabilityForm({ ...liabilityForm, institution: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder={t('netWorth:institutionPlaceholder', 'e.g., Chase Bank')}
                />
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('netWorth:notes', 'Notes')}
                </label>
                <textarea
                  value={liabilityForm.notes}
                  onChange={(e) => setLiabilityForm({ ...liabilityForm, notes: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  rows={2}
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button
                variant="secondary"
                onClick={() => { setShowAddLiabilityModal(false); setEditingLiability(null); }}
                fullWidth
              >
                {t('common:buttons.cancel', 'Cancel')}
              </Button>
              <Button
                onClick={handleAddLiability}
                fullWidth
                disabled={!liabilityForm.name || !liabilityForm.type_id || !liabilityForm.balance}
              >
                {editingLiability
                  ? t('common:buttons.save', 'Save')
                  : t('netWorth:addLiability', 'Add Liability')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NetWorthTracker;
